import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { AuthContext } from '../../contexts/AuthContext';
import SessionExpired from './SessionExpired';
import WarningDialog from './WarningDialog';
import { useNavigate } from 'react-router-dom';

function IdleTimer() {
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [idleState, setIdleState] = useState('active'); // Track idle state for debugging
  const { logout, refreshToken, isAuthenticated, userType } = useContext(AuthContext);
  const navigate = useNavigate();
  const warningTimeoutRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const showWarningRef = useRef(false); // Track warning state in ref to avoid stale closure

  // Check if this is a participant user (will have extended idle timeout)
  const isParticipant = userType === 'participant';
  
  // Check if we're running in local development
  const isLocalDevelopment = import.meta.env.DEV || 
                            window.location.hostname === 'localhost' || 
                            window.location.hostname === '127.0.0.1' ||
                            window.location.port === '5173';
  
  // Disable idle timer completely for local development
  if (isLocalDevelopment) {
    console.debug('IdleTimer disabled - running in local development environment');
    return null;
  }
  
  // Set timeout values based on user type - TEMPORARY TESTING VALUES
  const warningTimeout = isParticipant 
    ? 110 * 60 * 1000  // 110 minutes for participants
    : 25 * 60 * 1000;  // 25 minutes for regular users
    
  const logoutDelay = isParticipant 
    ? 10 * 60 * 1000   // Additional 10 minutes for participants
    : 5 * 60 * 1000;   // Additional 5 minutes for regular users

  const handleLogout = useCallback(async () => {
    console.log('User is idle, logging out...');
    await logout();
    setSessionExpired(true);
    console.log('Logged out due to inactivity');
    // Redirect to home page after logout
    navigate('/');
  }, [logout, navigate]);

  const resetTimer = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false; // Sync ref with state
    setSessionExpired(false);
    clearTimeout(warningTimeoutRef.current);
    if (idleTimerRef.current) {
      idleTimerRef.current.reset();
    }
  }, []);

  // Activity validation reference for real user activity
  const activityValidationRef = useRef(false);

  const handleKeyDown = useCallback(() => {
    activityValidationRef.current = true;
  }, []);

  // Register custom event handlers
  useEffect(() => {
    if (isAuthenticated) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('click', () => { activityValidationRef.current = true; });
      window.addEventListener('touchstart', () => { activityValidationRef.current = true; });
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', () => { activityValidationRef.current = true; });
      window.removeEventListener('touchstart', () => { activityValidationRef.current = true; });
    };
  }, [isAuthenticated, handleKeyDown]);

  const { getRemainingTime, getLastActiveTime, reset } = useIdleTimer({
    timeout: warningTimeout, // Dynamic timeout based on user type
    onIdle: () => {
      // Show warning for all authenticated users
      if (isAuthenticated && !showWarning) {
        const idleTime = Math.round((Date.now() - getLastActiveTime()) / 1000 / 60);
        console.log(`IDLE DETECTED: ${isParticipant ? 'Participant' : 'Regular'} user idle for ${idleTime} minutes, showing warning`);
        setIdleState('idle');
        setShowWarning(true);
        showWarningRef.current = true; // Sync ref for use in onActive callback
        // Set a timeout to logout after the appropriate delay
        warningTimeoutRef.current = setTimeout(handleLogout, logoutDelay);
      }
    },
    onActive: () => {
      // Only process real activity (validated by our custom handlers)
      if (activityValidationRef.current) {
        // Reset the validation flag
        activityValidationRef.current = false;
        
        // Track last activity time
        lastActivityRef.current = Date.now();
        
        if (idleState === 'idle') {
          console.log('User became active again');
          setIdleState('active');
        }
        
        // Only clear the warning if the user becomes active before the warning is shown
        // Use ref to avoid stale closure - showWarning state would be stale here
        if (!showWarningRef.current) {
          clearTimeout(warningTimeoutRef.current);
        }
      } else {
        // Log phantom activity detection
        console.log('Phantom activity detected and ignored');
      }
    },
    debounce: 2000, // Increased debounce to reduce sensitivity
    eventsThrottle: 500,
    events: [
      'keydown',
      'click',
      'touchstart',
      'touchmove'
    ]
  });

  // Store the reset function and other utilities in the ref
  idleTimerRef.current = { reset, getRemainingTime, getLastActiveTime };

  const handleExtendSession = useCallback(async () => {
    try {
      await refreshToken();
      resetTimer();
    } catch (error) {
      console.error('Failed to extend session:', error);
      handleLogout();
    }
  }, [refreshToken, handleLogout, resetTimer]);

  // Reset timer when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      console.log(`${isParticipant ? 'Participant' : 'Regular'} user authenticated, resetting timer`);
      console.log(`Timeout set to: ${warningTimeout/60000} minutes`);
      resetTimer();
    } else {
      setShowWarning(false);
      setSessionExpired(false);
    }
  }, [isAuthenticated, isParticipant, resetTimer, warningTimeout]);
  
  // Periodic debug logging
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const debugInterval = setInterval(() => {
      if (idleTimerRef.current && idleTimerRef.current.getRemainingTime) {
        const remaining = Math.round(idleTimerRef.current.getRemainingTime() / 1000 / 60);
        const lastActive = Math.round((Date.now() - idleTimerRef.current.getLastActiveTime()) / 1000 / 60);
        console.log(`DEBUG: Time remaining: ${remaining} minutes, Last activity: ${lastActive} minutes ago, State: ${idleState}, Warning shown: ${showWarning}`);
      }
    }, 5 * 60 * 1000); // Log every 5 minutes
    
    return () => clearInterval(debugInterval);
  }, [isAuthenticated, idleState, showWarning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(warningTimeoutRef.current);
      setShowWarning(false);
      setSessionExpired(false);
    };
  }, []);

  // Don't render the component for unauthenticated users
  if (!isAuthenticated) {
    return null;
  }

  if (sessionExpired) {
    return (
      <div className="session-expired-overlay">
        <SessionExpired />
      </div>
    );
  }

  return (
    <>
      {showWarning && (
        <WarningDialog
          onClose={() => {
            resetTimer();
          }}
          onExtendSession={handleExtendSession}
          onTimeout={handleLogout}
          timeoutMinutes={isParticipant ? 10 : 5}
        />
      )}
    </>
  );
}

export default IdleTimer;